import React, { useState, useMemo, useRef } from "react";
import { Box, Button, Select, Table, Thead, Tbody, Tr, Th, Td,  Text, HStack, VStack, Badge, Flex, Input, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import useViewAmbulance from "../hooks/ViewAmbulanceHook";

const ViewAmbulance: React.FC = () => {
    const { companys, selectedCompany, setSelectedCompany, handleSubmit, ambulanceCoverageData, page, setPage, limit, handleLimitChange,
        handlePreviousPage,
        handleNextPage, isLoading } = useViewAmbulance();
    
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    
    const filteredCompanies = useMemo(() => {
        return companys.filter(company => 
            company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [companys, searchTerm]);
    
    const selectedCompanyName = companys.find(c => c._id === selectedCompany)?.companyName || "";

    

    return (
        <Box>
            <VStack spacing={4}>
                <HStack spacing={4}>
                    <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
                        <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon />}
                            onClick={() => setIsOpen(!isOpen)}
                            width="300px"
                            textAlign="left"
                        >
                            {selectedCompanyName || "Select a company"}
                        </MenuButton>
                        <MenuList maxH="300px" overflowY="auto">
                            <Box p={2} onClick={(e) => e.stopPropagation()}>
                                <Input
                                    ref={inputRef}
                                    placeholder="Search companies..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setTimeout(() => inputRef.current?.focus(), 0);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                            e.preventDefault();
                                        }
                                    }}
                                    onFocus={(e) => e.stopPropagation()}
                                    size="sm"
                                    autoComplete="off"
                                />
                            </Box>
                            {filteredCompanies.map((company) => (
                                <MenuItem
                                    key={company._id}
                                    onClick={() => {
                                        setSelectedCompany(company._id);
                                        setSearchTerm("");
                                        setIsOpen(false);
                                    }}
                                >
                                    {company.companyName}
                                </MenuItem>
                            ))}
                            {filteredCompanies.length === 0 && (
                                <MenuItem isDisabled>
                                    No companies found
                                </MenuItem>
                            )}
                        </MenuList>
                    </Menu>
                    
                    <Button 
                        colorScheme="green" 
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        loadingText="Loading..."
                        disabled={isLoading}
                    >
                        View Coverage
                    </Button>
                </HStack>
                
                {
                    ambulanceCoverageData?.matchedData?.length>0 && (
                        <HStack justifyContent="flex-end" mb={4} w="full" mr={"10px"}>
                    <Select
                        value={limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                        width="120px"
                    >
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={300}>300</option>
                        <option value={400}>400</option>
                        <option value={500}>500</option>
                    </Select>
                    <Button
                        onClick={handlePreviousPage}
                        disabled={page <= 1}
                        size="sm"
                        colorScheme={page <= 1 ? "gray" : "green"}
                    >
                        Previous
                    </Button>
                    <Text>Page {page} of {ambulanceCoverageData.totalPages}</Text>
                    <Button
                        onClick={handleNextPage}
                        disabled={page >= (ambulanceCoverageData?.totalPages || 1)}
                        size="sm"
                        colorScheme={page >= (ambulanceCoverageData?.totalPages || 1) ? "gray" : "green"}
                    >
                        Next
                    </Button>
                    <Select
                        value={page}
                        onChange={(e) => setPage(Number(e.target.value))}
                        width="120px"
                        size="sm"
                    >
                        {Array.from({ length: ambulanceCoverageData?.totalPages || 1 }, (_, i) => i + 1).map((pageNum) => (
                            <option key={pageNum} value={pageNum}>
                                Page {pageNum}
                            </option>
                        ))}
                    </Select>
                </HStack> 
                    )
                }
                    
                
                {ambulanceCoverageData?.stats && (
                    <Box p={4} borderWidth={1} borderRadius="md" bg="gray.50" w="full">
                        <Text fontSize="lg" fontWeight="bold" mb={3}>Coverage Statistics</Text>
                        <Flex wrap="wrap" gap={4}>
                            <Badge colorScheme="blue" p={2} borderRadius="md">
                                Total Beneficiaries: {ambulanceCoverageData.totalBens}
                            </Badge>
                            <Badge colorScheme="green" p={2} borderRadius="md">
                                Matched Addresses: {ambulanceCoverageData.stats.matchedAddressCount}
                            </Badge>
                            <Badge colorScheme="red" p={2} borderRadius="md">
                                Unmatched Addresses: {ambulanceCoverageData.stats.unmatchedAddressCount}
                            </Badge>
                            <Badge colorScheme="orange" p={2} borderRadius="md">
                                No Address: {ambulanceCoverageData.stats.noAddressCount}
                            </Badge>
                            <Badge colorScheme="purple" p={2} borderRadius="md">
                                Page: {page} of {ambulanceCoverageData.totalPages}
                            </Badge>
                        </Flex>
                    </Box>
                )}
            </VStack>

            {ambulanceCoverageData?.matchedData && (
                <Box>
                    <Table variant="simple" size="sm" width="100%" style={{ tableLayout: 'fixed' }}>
                        <Thead>
                            <Tr>
                                <Th width="10%" fontSize="sm" fontWeight="bold">Name</Th>
                                <Th width="12%" fontSize="sm" fontWeight="bold">Email</Th>
                                <Th width="8%" fontSize="sm" fontWeight="bold">Ben ID</Th>
                                <Th width="8%" fontSize="sm" fontWeight="bold">Sub ID</Th>
                                <Th width="18%" fontSize="sm" fontWeight="bold">Address</Th>
                                <Th width="10%" fontSize="sm" fontWeight="bold">City</Th>
                                <Th width="6%" fontSize="sm" fontWeight="bold">Pin</Th>
                                <Th width="9%" fontSize="sm" fontWeight="bold">Exact Match</Th>
                                <Th width="9%" fontSize="sm" fontWeight="bold">Approx Match</Th>
                                <Th width="10%" fontSize="sm" fontWeight="bold">Ambulance Provider</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {ambulanceCoverageData.matchedData.map((beneficiary: any) => {
                                if (beneficiary.addresses.length === 0) {
                                    // Show beneficiary with empty address fields
                                    return (
                                        <Tr key={beneficiary.benId}>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{beneficiary.name}</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{beneficiary.email}</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{beneficiary.benId}</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{beneficiary.subScriberId || '-'}</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">-</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">No Address</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">-</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">-</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">-</Td>
                                            <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">-</Td>
                                        </Tr>
                                    );
                                }
                                // Show beneficiary with addresses
                                return beneficiary.addresses.map((address: any, addressIndex: number) => (
                                    <Tr key={`${beneficiary.benId}-${addressIndex}`}>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{addressIndex === 0 ? beneficiary.name : ''}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{addressIndex === 0 ? beneficiary.email : ''}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{addressIndex === 0 ? beneficiary.benId : ''}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{addressIndex === 0 ? (beneficiary.subScriberId || '-') : ''}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.address || '-'}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.city}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.pincode}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.exactMatchedCity || '-'}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.approxMatchedCity || '-'}</Td>
                                        <Td fontSize="md"  wordBreak="break-word" whiteSpace="normal">{address.ambulanceProvider}</Td>
                                    </Tr>
                                ));
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

export default ViewAmbulance;